import { ChangeEvent, FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { SaveButton } from "components/buttons/SaveButton"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"
import { AuthSettings } from "services/settings/type"

import { Tenant } from "types/api"

import { ResetToDefaultButton } from "./ResetToDefaultButton"
import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface EmailTabProps {
  auth: AuthSettings
  currentTenant: Tenant | null
  /** Called with the values the backend accepted; the tab edits a local draft until Save. */
  onChange: (auth: AuthSettings) => void
  onDirty: () => void
  onSaved: () => void
  onTenantChange: (tenant: Tenant) => void
  refreshSettings: () => Promise<void>
}

type ToggleValue = keyof AuthSettings

const AUTH_TOGGLES: ToggleValue[] = ["allowUnverifiedSignIn", "autoSignInAfterVerification", "marketingConsentEnabled"]

export const EmailTab: FC<EmailTabProps> = ({
  auth,
  currentTenant,
  onChange,
  onDirty,
  onSaved,
  onTenantChange,
  refreshSettings
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [savingProvider, setSavingProvider] = useState(false)
  const [draft, setDraft] = useState<AuthSettings>(auth)
  const [historyKey, setHistoryKey] = useState(0)
  const [selectedProvider, setSelectedProvider] = useState<"nodemailer" | "customerio">(
    currentTenant?.setup_data.emailDelivery?.provider ?? "nodemailer"
  )

  // `auth` only changes when saved values do (save, reset, refetch) — resync the draft then.
  useEffect(() => {
    setDraft(auth)
  }, [auth])

  useEffect(() => {
    setSelectedProvider(currentTenant?.setup_data.emailDelivery?.provider ?? "nodemailer")
  }, [currentTenant?.setup_data.emailDelivery?.provider])

  const handleToggle = (toggleValue: ToggleValue) => {
    setDraft((prev) => ({ ...prev, [toggleValue]: !prev[toggleValue] }))
    onDirty()
  }

  const handleSave = async () => {
    const changed = AUTH_TOGGLES.filter((key) => draft[key] !== auth[key])
    if (changed.length === 0) {
      onSaved()
      return
    }

    setSaving(true)
    try {
      const applied: Partial<AuthSettings> = {}
      let allSaved = true
      for (const key of changed) {
        const success = await settingsService.updateEmailSignIn(draft[key], key)
        if (!success) {
          allSaved = false
          break
        }
        applied[key] = draft[key]
      }

      if (Object.keys(applied).length > 0) {
        onChange({ ...auth, ...applied })
        setHistoryKey((key) => key + 1)
      }
      if (allSaved) {
        onSaved()
        toast.success(t("settings.email.updated") as string)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleEmailProviderChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedProvider(e.target.value as "nodemailer" | "customerio")
  }

  const handleSaveEmailProvider = async () => {
    setSavingProvider(true)
    try {
      const success = await settingsService.updateEmailProvider(selectedProvider)
      if (success) {
        toast.success(t("settings.email.providerUpdated") as string)
        if (currentTenant) {
          onTenantChange({
            ...currentTenant,
            setup_data: {
              ...currentTenant.setup_data,
              emailDelivery: { provider: selectedProvider }
            }
          })
        }
      }
    } finally {
      setSavingProvider(false)
    }
  }

  return (
    <section className="flex flex-col gap-6 mt-6">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.email.title")}</h3>
        <SettingsHistoryNote target="auth" refreshKey={historyKey} />
      </div>

      <div className="flex items-center justify-start gap-4 rounded-lg px-4 py-4 border border-black/10 dark:border-white/10 bg-white dark:bg-[#0C0C1A]">
        <span className="text-base font-semibold text-gray-800 dark:text-white">{t("settings.email.allowUnverified")}</span>
        <Toggle checked={draft.allowUnverifiedSignIn} onChange={() => handleToggle("allowUnverifiedSignIn")} disabled={saving} />
      </div>

      <div className="flex items-center justify-start gap-4 rounded-lg px-4 py-4 border border-black/10 dark:border-white/10 bg-white dark:bg-[#0C0C1A]">
        <span className="text-base font-semibold text-gray-800 dark:text-white">{t("settings.email.autoSignIn")}</span>
        <Toggle checked={draft.autoSignInAfterVerification} onChange={() => handleToggle("autoSignInAfterVerification")} disabled={saving} />
      </div>

      <div className="flex flex-col gap-1 rounded-lg px-4 py-4 border border-black/10 dark:border-white/10 bg-white dark:bg-[#0C0C1A]">
        <div className="flex items-center justify-start gap-4">
          <span className="text-base font-semibold text-gray-800 dark:text-white">{t("settings.email.marketingConsent")}</span>
          <Toggle checked={draft.marketingConsentEnabled} onChange={() => handleToggle("marketingConsentEnabled")} disabled={saving} />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">{t("settings.email.marketingConsentHint")}</p>
      </div>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="auth"
        resetLabel={t("settings.email.title")}
        onResetDone={refreshSettings}
      />

      {currentTenant && (
        <>
          <div className="max-w-md">
            <DropdownMenu
              title={t("settings.email.provider")}
              options={[
                { name: t("settings.email.providerInhouse"), value: "nodemailer" },
                { name: "Customer.io", value: "customerio" }
              ]}
              value={selectedProvider}
              onChange={handleEmailProviderChange}
              hideEmptyOption
              disableEmptyOption
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-3">
            <ResetToDefaultButton
              target="emailDelivery"
              label={t("settings.email.provider")}
              onDone={refreshSettings}
              showSectionInLabel
            />
            <SaveButton onClick={handleSaveEmailProvider} loading={savingProvider} className="w-auto min-w-[12rem] mr-0">
              {t("settings.email.saveProvider")}
            </SaveButton>
          </div>
        </>
      )}
    </section>
  )
}
